import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Wallet as WalletIcon, Plus, FileText, TrendingUp, TrendingDown,
  Loader2, CreditCard, AlertCircle,
} from "lucide-react";

const TX_TYPE_BADGE = {
  TOPUP: "bg-emerald-100 text-emerald-700 border-emerald-200",
  SHIPMENT: "bg-blue-100 text-blue-700 border-blue-200",
  REFUND: "bg-amber-100 text-amber-700 border-amber-200",
  MANUAL: "bg-purple-100 text-purple-700 border-purple-200",
  PAYMENT: "bg-slate-200 text-slate-700 border-slate-300",
  ADJUSTMENT: "bg-slate-200 text-slate-700 border-slate-300",
};

export default function CustomerWallet() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get(`/v2/wallets/${user.id}/summary`),
        api.get(`/v2/wallets/${user.id}/transactions?limit=500`).catch(() =>
          api.get(`/wallet/transactions`).catch(() => ({ data: [] }))
        ),
      ]);
      setSummary(s.data);
      const list = Array.isArray(t.data) ? t.data : t.data?.items || [];
      setTxs(list);
    } catch (e) {
      toast.error("Failed to load wallet");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const balance = Number(summary?.current_balance || 0);
  const isCredit = summary?.wallet_type === "CREDIT";
  const creditLimit = Number(summary?.credit_limit || 0);
  const creditUsed = Number(summary?.credit_used || Math.max(-balance, 0));
  const creditAvail = Math.max(creditLimit - creditUsed, 0);
  const creditWalletEnabled = summary?.credit_wallet_enabled !== false;

  const filtered = (filter === "ALL"
    ? txs
    : txs.filter((tx) => (tx.type || tx.txn_type || "").toUpperCase().startsWith(filter))
  ).slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(
    1,
    Math.ceil(
      (filter === "ALL"
        ? txs.length
        : txs.filter((tx) => (tx.type || tx.txn_type || "").toUpperCase().startsWith(filter))
            .length) / PAGE_SIZE
    )
  );

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="customer-wallet-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Wallet</h1>
            <p className="text-sm text-slate-500">
              Manage your balance, top-ups and transaction history
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate("/customer/wallet/topup")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="add-money-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Money
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/customer/invoices")}
              data-testid="view-invoices-btn"
            >
              <FileText className="w-4 h-4 mr-1" /> Invoices
            </Button>
          </div>
        </div>

        {/* Disabled banner */}
        {isCredit && !creditWalletEnabled && (
          <div className="bg-red-50 border border-red-200 rounded-md px-4 py-2 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Credit wallet is disabled for this account. Please contact support.
          </div>
        )}

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <StatCard
            label="Current Balance"
            value={loading ? "…" : `₹${balance.toFixed(2)}`}
            color={balance >= 0 ? "emerald" : "red"}
            big
          />
          <StatCard
            label="Total Topped Up"
            value={loading ? "…" : `₹${Number(summary?.total_credit || 0).toFixed(2)}`}
            icon={<TrendingUp className="w-4 h-4" />}
            color="blue"
          />
          <StatCard
            label="Total Spent"
            value={loading ? "…" : `₹${Number(summary?.total_debit || 0).toFixed(2)}`}
            icon={<TrendingDown className="w-4 h-4" />}
            color="slate"
          />
          <StatCard
            label="Pending Invoices"
            value={loading ? "…" : `₹${Number(summary?.pending_invoices_amount || 0).toFixed(2)}`}
            color="amber"
          />
        </div>

        {/* Credit bar */}
        {isCredit && creditLimit > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-sm font-semibold text-slate-700">
                Credit Used: ₹{creditUsed.toFixed(2)} of ₹{creditLimit.toFixed(2)}
              </div>
              <span
                className={`text-[10px] font-bold px-2 py-1 rounded border ${
                  creditWalletEnabled
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                    : "bg-red-100 text-red-700 border-red-200"
                }`}
                data-testid="credit-status"
              >
                {creditWalletEnabled ? "ENABLED" : "DISABLED"}
              </span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded overflow-hidden">
              <div
                className={`h-2 ${
                  creditUsed / creditLimit < 0.5
                    ? "bg-emerald-500"
                    : creditUsed / creditLimit < 0.85
                    ? "bg-amber-500"
                    : "bg-red-500"
                }`}
                style={{ width: `${Math.min(100, (creditUsed / creditLimit) * 100)}%` }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              Available credit: ₹{creditAvail.toFixed(2)}
            </p>
          </div>
        )}

        {/* Transaction history */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between p-3 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-800">Transaction History</h3>
            <select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(1);
              }}
              className="border border-slate-300 rounded h-9 px-2 text-xs bg-white"
              data-testid="tx-filter"
            >
              <option value="ALL">All</option>
              <option value="TOPUP">Topup</option>
              <option value="SHIPMENT">Shipment</option>
              <option value="REFUND">Refund</option>
              <option value="MANUAL">Manual</option>
            </select>
          </div>
          {loading ? (
            <div className="py-12 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-slate-400">
              <WalletIcon className="w-10 h-10 mx-auto mb-2 opacity-60" />
              <p className="text-sm">No transactions yet</p>
            </div>
          ) : (
            <>
              <table className="w-full text-sm" data-testid="customer-tx-table">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-left p-3">Description</th>
                    <th className="text-right p-3">Amount</th>
                    <th className="text-right p-3">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((tx, idx) => {
                    const t = (tx.type || tx.txn_type || "OTHER").toUpperCase();
                    const isCredit = Number(tx.amount || 0) > 0;
                    const badgeKey = Object.keys(TX_TYPE_BADGE).find((k) => t.startsWith(k));
                    return (
                      <tr key={tx.id || idx} className="hover:bg-slate-50">
                        <td className="p-3 text-xs text-slate-600">
                          {new Date(tx.created_at || tx.date || Date.now()).toLocaleDateString()}
                        </td>
                        <td className="p-3">
                          <span
                            className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded border ${
                              TX_TYPE_BADGE[badgeKey] || "bg-slate-100 text-slate-600 border-slate-200"
                            }`}
                          >
                            {t}
                          </span>
                        </td>
                        <td className="p-3 text-xs text-slate-700">
                          {tx.description || tx.reason || tx.reference || "—"}
                        </td>
                        <td
                          className={`p-3 text-right font-semibold ${
                            isCredit ? "text-emerald-700" : "text-red-700"
                          }`}
                        >
                          {isCredit ? "+" : ""}₹{Math.abs(Number(tx.amount || 0)).toFixed(2)}
                        </td>
                        <td className="p-3 text-right text-slate-600 text-xs">
                          {tx.balance_after != null
                            ? `₹${Number(tx.balance_after).toFixed(2)}`
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="p-3 flex items-center justify-between text-xs text-slate-500 border-t border-slate-100">
                  <span>
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      Prev
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ label, value, color = "slate", big, icon }) {
  const c = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    red: "bg-red-50 border-red-200 text-red-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[color];
  return (
    <div className={`border rounded-xl p-4 ${c}`}>
      <div className="text-[10px] uppercase font-bold tracking-wider opacity-80 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className={`font-bold mt-1 ${big ? "text-3xl" : "text-xl"}`}>{value}</div>
    </div>
  );
}
